import MediaImage from "@/components/ui/MediaImage";
import React from "react";
import { richTextFontStyles } from "@/utils/rich-text-fonts";
import { richTextLineHeight } from "@/utils/line-height";
import parse, {
  Element,
  domToReact,
  type HTMLReactParserOptions,
} from "html-react-parser";

export const hasRichTextMarker = (html: string) =>
  /<div\s[^>]*data-reweb-rich-text=["']1["']/i.test(html);

const tags = new Set([
  "div",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "s",
  "sub",
  "sup",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "br",
  "hr",
  "table",
  "colgroup",
  "col",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "code",
  "pre",
  "span",
  "img",
]);
/** Headings and titles: marks and line breaks only, so the text stays valid inside an h1–h3 or a link. */
const inlineTags = new Set([
  "strong",
  "em",
  "b",
  "i",
  "u",
  "s",
  "sub",
  "sup",
  "br",
  "span",
]);
const discard = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "svg",
  "math",
]);

function link(value: string | undefined, image = false): string | undefined {
  if (
    !value ||
    Array.from(value).some(
      (character) =>
        character.charCodeAt(0) <= 32 ||
        character.charCodeAt(0) === 127 ||
        character === "\\",
    ) ||
    value.startsWith("//")
  )
    return;
  if (/^(?:https?:\/\/|\/(?!\/))/i.test(value)) return value;
  if (!image && /^(?:mailto:|tel:|#)/i.test(value)) return value;
  if (
    image &&
    /^data:image\/(?:png|jpeg|gif|webp);base64,[\da-z+/=]+$/i.test(value)
  )
    return value;
  if (!image && !value.includes(":")) return value;
}

export function richTextStyles(raw = ""): React.CSSProperties {
  const output: Record<string, string> = {};
  for (const part of raw.split(";")) {
    const colon = part.indexOf(":");
    if (colon < 0) continue;
    const name = part.slice(0, colon).trim().toLowerCase();
    const value = part.slice(colon + 1).trim();
    if (/url\s*\(|expression\s*\(|[<>\\]/i.test(value)) continue;
    if (
      ["color", "background-color"].includes(name) &&
      /^(?:#[\da-f]{3,8}|[a-z]+|(?:rgb|hsl)a?\([\d.,% /+-]+\)|var\(--[\w-]+\))$/i.test(
        value,
      )
    )
      output[name === "color" ? "color" : "backgroundColor"] = value;
    if (
      name === "font-family" &&
      (/^[\w\s,'"-]+$/.test(value) || richTextFontStyles.has(value))
    )
      output.fontFamily = value;
    if (name === "line-height" && richTextLineHeight(value))
      output.lineHeight = value;
    if (
      ["font-size", "width"].includes(name) &&
      /^(?:\d+(?:\.\d+)?(?:px|rem|em|%)?|auto|normal)$/.test(value)
    )
      output[
        name.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
      ] = value;
    if (
      name === "text-align" &&
      ["left", "center", "right", "justify"].includes(value)
    )
      output.textAlign = value;
  }
  return output;
}

/** Only editor-produced attributes survive. Unknown containers retain text; active content is discarded. */
export default function RichTextContent({
  html,
  idPrefix,
  inline = false,
}: {
  html: string;
  idPrefix?: string;
  inline?: boolean;
}) {
  const allowed = inline ? inlineTags : tags;
  let heading = 0;
  const options: HTMLReactParserOptions = {
    replace(node) {
      if (!(node instanceof Element)) return;
      if (discard.has(node.name)) return <React.Fragment />;
      const children = () =>
        domToReact(node.children as Parameters<typeof domToReact>[0], options);
      if (!allowed.has(node.name))
        return <React.Fragment>{children()}</React.Fragment>;
      const attrs: Record<string, unknown> = {};
      const style = richTextStyles(node.attribs.style);
      if (Object.keys(style).length) attrs.style = style;
      const classes = (node.attribs.class || "")
        .split(/\s+/)
        .filter((value) =>
          /^homepage-text-(?:xs|sm|md|lg|xl|2xl|4xl)$/.test(value),
        );
      if (classes.length) attrs.className = classes.join(" ");
      if (node.attribs["data-reweb-rich-text"] === "1")
        attrs["data-reweb-rich-text"] = "1";
      if (/^h[1-6]$/.test(node.name) && idPrefix)
        attrs.id = `${idPrefix}-heading-${heading++}`;
      if (node.name === "a") {
        const href = link(node.attribs.href);
        if (href) attrs.href = href;
        if (node.attribs.title) attrs.title = node.attribs.title;
        if (node.attribs.target === "_blank") {
          attrs.target = "_blank";
          attrs.rel = "noopener noreferrer";
        }
      }
      if (node.name === "img") {
        const src = link(node.attribs.src, true);
        if (!src) return <React.Fragment />;
        return (
          <MediaImage
            src={src}
            alt="billede"
            legacyAlt={node.attribs.alt}
            role={
              node.attribs.role === "presentation" ? "presentation" : undefined
            }
            aria-hidden={
              node.attribs["aria-hidden"] === "true" ? true : undefined
            }
            title={node.attribs.title}
            loading="lazy"
          />
        );
      }
      for (const [source, target] of [
        ["colspan", "colSpan"],
        ["rowspan", "rowSpan"],
        ["span", "span"],
        ["start", "start"],
      ]) {
        if (/^\d{1,4}$/.test(node.attribs[source] || ""))
          attrs[target] = Number(node.attribs[source]);
      }
      if (node.name === "br" || node.name === "hr" || node.name === "col")
        return React.createElement(node.name, attrs);
      return React.createElement(node.name, attrs, children());
    },
  };
  return <React.Fragment>{parse(html, options)}</React.Fragment>;
}
